import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import {
  FilterFieldConfig,
  FilterRule,
} from '../../view-model/InfluencerList.filterTypes';
import {
  FilterPreset,
  InfluencerListVM,
} from '../../view-model/InfluencerList.viewModel';
import { toFilterDisplayText } from '../../view-model/InfluencerList.viewModel.mappers';
import { FilterChip } from './FilterChip';
import { AddFilterButton } from './AddFilterButton';
import * as styles from './FilterBar.css';

type FilterBarProps = {
  filterRules: FilterRule[];
  filterFieldConfigs: FilterFieldConfig[];
  filterPresets: FilterPreset[];
  dispatch: InfluencerListVM['dispatch'];
};

type PresetsDropdownProps = {
  presets: FilterPreset[];
  dispatch: InfluencerListVM['dispatch'];
};

const PresetsDropdown = ({ presets, dispatch }: PresetsDropdownProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={wrapperRef} className={styles.presetWrapper}>
      <button
        type="button"
        className={styles.presetButton}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        Presets ({presets.length}){' '}
        <FontAwesomeIcon icon={faChevronDown} size="xs" />
      </button>
      {open ? (
        <div className={styles.presetPopover}>
          {presets.map((preset) => (
            <div key={preset.id} className={styles.presetItem}>
              <button
                type="button"
                className={styles.presetLoadButton}
                onClick={() => {
                  dispatch('LoadFilterPreset', preset.id);
                  setOpen(false);
                }}
              >
                {preset.name}
              </button>
              <button
                type="button"
                className={styles.presetDeleteButton}
                onClick={() => dispatch('DeleteFilterPreset', preset.id)}
                aria-label={`Delete preset ${preset.name}`}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const hasActions = (filterRules: FilterRule[], filterPresets: FilterPreset[]) =>
  filterRules.length > 0 || filterPresets.length > 0;

export const FilterBar = ({
  filterRules,
  filterFieldConfigs,
  filterPresets,
  dispatch,
}: FilterBarProps) => (
  <div className={styles.filterBar}>
    <div className={styles.filterBarLeft}>
      {filterRules.map((rule) => (
        <FilterChip
          key={rule.id}
          displayText={toFilterDisplayText(rule)}
          onRemove={() => dispatch('RemoveFilterRule', rule.id)}
        />
      ))}
      <AddFilterButton
        fieldConfigs={filterFieldConfigs}
        onAdd={(rule) => dispatch('AddFilterRule', rule)}
      />
    </div>
    {hasActions(filterRules, filterPresets) ? (
      <>
        <div className={styles.divider} />
        <div className={styles.filterBarRight}>
          {filterRules.length > 0 ? (
            <button
              type="button"
              className={styles.savePresetButton}
              onClick={() => {
                const name = window.prompt('Name this filter preset:');
                if (name?.trim()) {
                  dispatch('SaveFilterPreset', name.trim());
                }
              }}
            >
              Save filters
            </button>
          ) : null}
          {filterPresets.length > 0 ? (
            <PresetsDropdown presets={filterPresets} dispatch={dispatch} />
          ) : null}
          {filterRules.length > 0 ? (
            <button
              type="button"
              className={styles.clearAllButton}
              onClick={() => dispatch('ClearAllFilters')}
            >
              Clear all
            </button>
          ) : null}
        </div>
      </>
    ) : null}
  </div>
);
